'use client';

import { NavigableButton } from '@carvana/ds-button';
import AppCheck from '@carvana/ds-icons/AppCheckSpot';
import BudgetFriendly from '@carvana/ds-icons/BudgetFriendlySpot';
import SevenDay from '@carvana/ds-icons/SevenDay';
import SmileyCar from '@carvana/ds-icons/SmileyCarSpot';
import { icon } from '@carvana/ds-styles';
import Image from '@/common/Image';
import dealershipFamilyMap from '~/constants/dealership/dealershipFamilyMap';
import { appAssetsCdnUrl } from '~/constants/environment';
import { useDealershipId } from '~/context/dealership/StaticContext';
import {
  LowerSection,
  ShadedBackground,
  Tile,
  TileDivider,
  TileHeading,
  TilesWrapper,
  TileText
} from './Hero.styles';

export default function Hero() {
  const dealershipId = useDealershipId();

  const dealershipFamily = dealershipId
    ? dealershipFamilyMap[dealershipId]
    : null;

  return (
    <>
      <div className="grid">
        <div className="z-[-1] col-span-full row-span-full flex">
          <Image
            src={`${appAssetsCdnUrl}/images/dealership/${dealershipFamily}/hero-desktop.jpg`}
            optimize={{ crop: '21:9' }}
            width={21 * 100}
            height={9 * 100}
            alt="Hero"
            className="w-full object-cover"
            wrapperClassName="aspect-[21/9] hidden w-full lg:grid"
            loading="lazy"
          />
          <Image
            src={`${appAssetsCdnUrl}/images/dealership/${dealershipFamily}/hero-desktop.jpg`}
            optimize={{ crop: '16:9' }}
            width={16 * 100}
            height={9 * 100}
            alt="Hero"
            className="w-full object-cover"
            wrapperClassName="aspect-[16/9] hidden w-full md:grid lg:hidden"
            loading="lazy"
          />
          <Image
            src={`${appAssetsCdnUrl}/images/dealership/${dealershipFamily}/hero-mobile.jpg`}
            optimize={{ crop: '2:3' }}
            width={2 * 100}
            height={3 * 100}
            alt="Hero"
            className="w-full object-cover"
            wrapperClassName="aspect-[2/3] md:hidden w-full"
            loading="lazy"
          />
        </div>
        <ShadedBackground />
        <div className="col-span-full row-span-full flex px-24 pt-32 md:items-center md:px-32 lg:px-[96px]">
          <div className="flex w-full flex-col items-center gap-y-2xl text-center md:items-start md:text-left">
            <h1 className="text-text-inverse t-brand-sm md:t-brand-md lg:t-brand-lg">
              The new way to
              <br />
              buy a new car
            </h1>
            <p className="text-text-inverse t-body-md lg:t-body-lg">
              Test drive on-site, buy online and get it delivered.
            </p>
            <NavigableButton
              variant="primary"
              size="md"
              className="w-full md:w-fit"
              href="/cars"
              data-adobe-id="shop-now"
              data-analytics-id="Shop now button"
              data-analytics-attributes={JSON.stringify({
                section: 'Hero'
              })}
              data-testid="shop-now"
            >
              Shop now
            </NavigableButton>
          </div>
        </div>
      </div>
      <LowerSection>
        <TilesWrapper>
          <Tile>
            <BudgetFriendly color={icon.inverse} size={56} />
            <TileHeading>Transparent pricing</TileHeading>
            <TileText>
              Zero doc fees, no hidden fees. See what you’ll pay up front.
            </TileText>
          </Tile>
          <TileDivider />
          <Tile>
            <SmileyCar color={icon.inverse} size={56} />
            <TileHeading>Stress-free test drive</TileHeading>
            <TileText>
              No upselling, take it for a spin before you commit.
            </TileText>
          </Tile>
          <TileDivider />
          <Tile>
            <AppCheck color={icon.inverse} size={56} />
            <TileHeading>Buy from anywhere</TileHeading>
            <TileText>
              Buy from our store, or from your couch. We’ll deliver it to your
              driveway.
            </TileText>
          </Tile>
          <TileDivider />
          <Tile>
            <SevenDay color={icon.inverse} size={56} />
            <TileHeading>7-Day Money-Back Guarantee</TileHeading>
            <TileText>
              Try it for a week. If it’s not the right fit, we’ll take it
              back—no questions asked.
            </TileText>
          </Tile>
        </TilesWrapper>
      </LowerSection>
    </>
  );
}
